/* eslint-disable prefer-destructuring,func-names,prefer-arrow-callback */
import axios from 'axios';
import _ from 'lodash';
import definitions from '../../../../API_MOCKS/definition/index.json';
import PredefVocabulary from '../PredefVocabularyRecord';

const API_URL = process.env.API_URL;
const NODE_ENV = process.env.NODE_ENV;

/**
 * these methods connect/simulate REST APIs. its up to redux to call and sync with them.
 *
 * @param bottle
 */

export default (bottle) => {
  bottle.constant('customVocabularyDataURI', `${API_URL}/definition/vocabularies`);

  bottle.constant('customVocabularySpecData', definitions);

  bottle.factory('headers', () => token => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }));

  bottle.factory('recordToID', () => (record) => {
    if (record) {
      if (typeof record === 'string') {
        return record;
      }
      if (typeof record === 'object') {
        return _.get(record, 'vocabularyName', null);
      }
    }
    return null;
  });

  bottle.factory('customVocabularyModel', ({ customVocabularySpecData }) => {
    const model = {
      get data() {
        const out = _.cloneDeep(model._data);
        out.vocabularies = out.vocabularies.map(r => new PredefVocabulary(r));
        return out;
      },
      _data: _.cloneDeep(customVocabularySpecData),
      setVocabularies(data) {
        model._data.vocabularies = data.map(record => new PredefVocabulary(record));
      },
      get(vocabularyName) {
        return _.find(model.data.vocabularies, { vocabularyName });
      },
      add(record) {
        const vocabulary = Object.assign({ type: 'term' }, new PredefVocabulary(record));
        if (!vocabulary.vocabularyName) {
          throw new Error('no name');
        }
        model.removeIDs([record.vocabularyName]);
        model._data.vocabularies = [...model.data.vocabularies, vocabulary];
      },
      removeIDs(ids) {
        const goodIds = ids.filter(_.identity).filter(a => typeof a === 'string');
        model._data.vocabularies = _.reject(model.data.vocabularies, v => goodIds.includes(v.vocabularyName));
      },
    };
    return model;
  });

  bottle.factory('customVocabularyAddRecord', function ({ customVocabularyDataURI, customVocabularyModel, headers }) {
    return async (record, token) => {
      const vocabulary = new PredefVocabulary(record);
      if (!vocabulary.vocabularyName) {
        throw new Error(`data missing vocabularyName, ${record}`);
      }
      if (NODE_ENV === 'test') {
        return customVocabularyModel.add(vocabulary);
      }

      return axios.put(
        `${customVocabularyDataURI}/${encodeURIComponent(vocabulary.vocabularyName)}`,
        vocabulary.toJSON(),
        { headers: headers(token) },
      );
    };
  });

  /**
   * note - the revision might include name change, so
   * vocabularyRecord doesn't necessarily equal newRecord.vocabularyName
   */
  bottle.factory('customVocabularyUpdateRecord', function ({
    customVocabularyModel,
    customVocabularyAddRecord, customVocabularyRemoveRecords, recordToID,
  }) {
    return async (vocabularyName, newRecord, token) => {
      if (NODE_ENV === 'test') {
        customVocabularyModel.removeIDs(vocabularyName);
        return customVocabularyModel.add(newRecord);
      }
      const out = await customVocabularyAddRecord(newRecord, token);
      if (vocabularyName !== 'new' && newRecord.vocabularyName !== vocabularyName) {
        return customVocabularyRemoveRecords(vocabularyName, token);
      }
      return out;
    };
  });

  bottle.factory('customVocabularyFetchAll', function ({
    customVocabularyModel, customVocabularyDataURI, headers,
  }) {
    return async (token) => {
      if (NODE_ENV === 'test') {
        return customVocabularyModel.data;
      }

      const result = await axios.get(
        customVocabularyDataURI,
        { headers: headers(token) },
      );

      return _.get(result.data, 'vocabularies', []) || [];
    };
  });

  /**
   * removes one record or a series of records.
   *
   * Acceptable inputs:
   * a vocabulary (object with a vocabularyName)
   * a string (vocabulary name)
   * an array of the above
   */
  bottle.factory('customVocabularyRemoveRecords', function ({
    customVocabularyModel,
    customVocabularyDataURI,
    headers,
    recordToID,
  }) {
    return async (records, token) => {
      const list = Array.isArray(records) ? records : [records];
      const ids = list.map(recordToID).filter(_.identity);

      if (NODE_ENV === 'test') {
        return customVocabularyModel.removeIDs(ids);
      }

      return await Promise.all(ids.map(id => axios.delete(
        `${customVocabularyDataURI}/${encodeURIComponent(id)}`,
        { headers: headers(token) },
      )));
    };
  });

  bottle.factory('customVocabularyFetchRecord', function ({
    headers, recordToID, customVocabularyDataURI, customVocabularyModel,
  }) {
    return async (recordOrName, token) => {
      const vocabularyName = recordToID(recordOrName);
      if (!vocabularyName) {
        throw new Error(`cannot get ${recordOrName}: no vocabularyName`);
      }

      if (NODE_ENV === 'test') {
        return customVocabularyModel.get(vocabularyName);
      }

      const url = `${customVocabularyDataURI}/${encodeURIComponent(vocabularyName)}`;
      console.log('getting ', url);

      const { data } = await axios.get(
        url,
        { headers: headers(token) },
      );

      return new PredefVocabulary(data);
    };
  });
};
